import { useCallback, useEffect, useRef, useState } from "react";
import type { BrandingConfig, ColorTheme } from "../types";
import { setBranding } from "../branding";
import { SkeletonList } from "./Skeleton";

const COLOR_THEME_OPTIONS: { value: ColorTheme; label: string; swatch: string }[] = [
  { value: "laranja", label: "Laranja", swatch: "#c15f3c" },
  { value: "vermelho", label: "Vermelho", swatch: "#c0392b" },
  { value: "azul", label: "Azul", swatch: "#2f6fed" },
  { value: "verde", label: "Verde", swatch: "#2f9e5c" },
];

/** Converte um File em data URL (base64) pra guardar o logo direto no documento de config. */
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("Falha ao ler o arquivo."));
    reader.readAsDataURL(file);
  });
}

/** Campos de texto simples (uma linha). */
const TEXT_FIELDS: { key: keyof BrandingConfig; label: string; placeholder?: string }[] = [
  { key: "appName", label: "Nome do app", placeholder: "MOC Assistant" },
  { key: "companyName", label: "Empresa / produto dono do workspace", placeholder: "MOC Soluções" },
  { key: "ownerName", label: "Nome de quem opera esta instância", placeholder: "Maike Carvalho" },
  { key: "ownerRole", label: "Papel do dono (como aparece no system prompt)", placeholder: "engenheira da MOC Soluções" },
  { key: "workspaceLabel", label: "Rótulo curto do workspace", placeholder: "MOC" },
];

/** Campos markdown (bloco livre) - textarea maior. */
const MARKDOWN_FIELDS: { key: keyof BrandingConfig; label: string; hint: string; rows: number }[] = [
  {
    key: "workspaceOverview",
    label: "Estrutura do workspace (markdown)",
    hint: "Vai direto no system prompt, seção \"Estrutura do workspace (visão rápida)\".",
    rows: 10,
  },
  {
    key: "customGuidance",
    label: "Orientações extras (markdown, opcional)",
    hint: "Parágrafo(s) adicionais de instrução específicos deste dono/workflow. Deixe vazio se não precisar.",
    rows: 4,
  },
];

/** Campos de lista (uma pergunta por linha). */
const LIST_FIELDS: { key: keyof BrandingConfig; label: string; hint: string }[] = [
  {
    key: "exampleQuestionsBusiness",
    label: "Perguntas de exemplo — tela inicial, modo negócio",
    hint: "Uma pergunta por linha.",
  },
  {
    key: "exampleQuestionsTechnical",
    label: "Perguntas de exemplo — tela inicial, modo técnico",
    hint: "Uma pergunta por linha.",
  },
  {
    key: "aboutExampleQuestions",
    label: 'Perguntas de exemplo — tela "Sobre" ("Boas perguntas pra começar")',
    hint: "Uma pergunta por linha.",
  },
];

type FormState = Record<keyof BrandingConfig, string>;

function toFormState(cfg: BrandingConfig): FormState {
  const state = {} as FormState;
  for (const { key } of TEXT_FIELDS) state[key] = String(cfg[key] ?? "");
  for (const { key } of MARKDOWN_FIELDS) state[key] = String(cfg[key] ?? "");
  for (const { key } of LIST_FIELDS) state[key] = (cfg[key] as string[] | undefined)?.join("\n") ?? "";
  state.colorTheme = cfg.colorTheme ?? "laranja";
  state.logoDataUrl = cfg.logoDataUrl ?? "";
  return state;
}

export function BrandingSettings() {
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch("/api/admin/branding");
      if (!r.ok) throw new Error("Falha ao carregar a configuração.");
      const cfg = (await r.json()) as BrandingConfig;
      setForm(toFormState(cfg));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao carregar a configuração.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setField = (key: keyof BrandingConfig, value: string) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSaved(false);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || saving) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const body: Record<string, string | string[]> = {};
      for (const { key } of TEXT_FIELDS) body[key] = form[key];
      for (const { key } of MARKDOWN_FIELDS) body[key] = form[key];
      for (const { key } of LIST_FIELDS) {
        body[key] = form[key]
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
      }
      body.colorTheme = form.colorTheme;
      body.logoDataUrl = form.logoDataUrl;
      const r = await fetch("/api/admin/branding", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? "Falha ao salvar.");
      }
      const updated = (await r.json()) as BrandingConfig;
      setForm(toFormState(updated));
      // reflete na hora no header/sidebar/tela "Sobre" desta mesma aba
      setBranding(updated);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const onLogoPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite escolher o mesmo arquivo de novo depois
    if (!file) return;
    setLogoError(null);
    if (file.size > 1_500_000) {
      setLogoError("Imagem muito grande (máx. ~1.5MB) - use um PNG/SVG menor.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setField("logoDataUrl", dataUrl);
    } catch {
      setLogoError("Falha ao ler o arquivo.");
    }
  };

  if (loading || !form) {
    return (
      <div className="admin-block">
        <SkeletonList rows={6} />
      </div>
    );
  }

  return (
    <form className="admin-block" onSubmit={save}>
      <h2>Identidade do assistente</h2>
      <p className="admin-sub">
        Textos que hoje aparecem fixos no app (nome, empresa, dono, estrutura do workspace, perguntas de
        exemplo) - editáveis aqui, sem mexer no banco nem refazer build da imagem. Vale só para esta
        instância.
      </p>

      {error && <div className="auth-expired" style={{ marginBottom: 12 }}>{error}</div>}
      {saved && !error && (
        <div className="admin-empty small" style={{ color: "var(--success, #2a9d5c)", marginBottom: 12 }}>
          Configuração salva.
        </div>
      )}

      {TEXT_FIELDS.map(({ key, label, placeholder }) => (
        <div key={key} style={{ marginBottom: 14 }}>
          <label htmlFor={`bs-${key}`} style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            {label}
          </label>
          <input
            id={`bs-${key}`}
            type="text"
            value={form[key]}
            placeholder={placeholder}
            onChange={(e) => setField(key, e.target.value)}
            style={{ width: "100%", padding: "6px 8px", fontSize: 13 }}
          />
        </div>
      ))}

      {MARKDOWN_FIELDS.map(({ key, label, hint, rows }) => (
        <div key={key} style={{ marginBottom: 14 }}>
          <label htmlFor={`bs-${key}`} style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            {label}
          </label>
          <p className="admin-user-meta" style={{ marginBottom: 4 }}>{hint}</p>
          <textarea
            id={`bs-${key}`}
            value={form[key]}
            rows={rows}
            onChange={(e) => setField(key, e.target.value)}
            style={{ width: "100%", padding: "6px 8px", fontSize: 13, fontFamily: "monospace" }}
          />
        </div>
      ))}

      {LIST_FIELDS.map(({ key, label, hint }) => (
        <div key={key} style={{ marginBottom: 14 }}>
          <label htmlFor={`bs-${key}`} style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            {label}
          </label>
          <p className="admin-user-meta" style={{ marginBottom: 4 }}>{hint}</p>
          <textarea
            id={`bs-${key}`}
            value={form[key]}
            rows={5}
            onChange={(e) => setField(key, e.target.value)}
            style={{ width: "100%", padding: "6px 8px", fontSize: 13 }}
          />
        </div>
      ))}

      <div style={{ marginBottom: 14 }}>
        <label htmlFor="bs-colorTheme" style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
          Tema de cor (accent da UI)
        </label>
        <select
          id="bs-colorTheme"
          value={form.colorTheme}
          onChange={(e) => setField("colorTheme", e.target.value)}
          style={{ width: "100%", padding: "6px 8px", fontSize: 13 }}
        >
          {COLOR_THEME_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          {COLOR_THEME_OPTIONS.map((opt) => (
            <span
              key={opt.value}
              title={opt.label}
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: opt.swatch,
                border: form.colorTheme === opt.value ? "2px solid var(--text)" : "1px solid var(--border)",
                cursor: "pointer",
              }}
              onClick={() => setField("colorTheme", opt.value)}
            />
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
          Logo (login e header, quando logado)
        </label>
        <p className="admin-user-meta" style={{ marginBottom: 4 }}>
          PNG ou SVG pequeno (até ~1.5MB). Vazio = usa o círculo genérico com a inicial do nome.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {form.logoDataUrl ? (
            <img
              src={form.logoDataUrl}
              alt="Logo atual"
              style={{ height: 32, maxWidth: 160, objectFit: "contain", background: "var(--bg-elevated)", borderRadius: 6, padding: 2 }}
            />
          ) : (
            <span className="admin-user-meta">(nenhum logo definido)</span>
          )}
          <button type="button" className="btn-reject" onClick={() => fileInputRef.current?.click()}>
            {form.logoDataUrl ? "Trocar logo" : "Enviar logo"}
          </button>
          {form.logoDataUrl && (
            <button type="button" className="btn-reject" onClick={() => setField("logoDataUrl", "")}>
              Remover
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/svg+xml,image/jpeg,image/webp"
            style={{ display: "none" }}
            onChange={(e) => void onLogoPicked(e)}
          />
        </div>
        {logoError && <div className="auth-expired" style={{ marginTop: 6 }}>{logoError}</div>}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn-approve" type="submit" disabled={saving}>
          {saving ? "Salvando…" : "Salvar"}
        </button>
        <button className="btn-reject" type="button" disabled={saving} onClick={() => void load()}>
          Descartar alterações
        </button>
      </div>
    </form>
  );
}
